'use client'

import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'

/** Fallback floor when the textarea has no computed `min-height` (jsdom). */
const FALLBACK_MIN_HEIGHT = 60

/** Height change per Arrow Up / Arrow Down press on the handle. */
const KEYBOARD_STEP = 20

const parsePixels = (value: string) => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getMinHeight = (textarea: HTMLTextAreaElement) => {
  const minHeight = parsePixels(window.getComputedStyle(textarea).minHeight)
  return minHeight > 0 ? minHeight : FALLBACK_MIN_HEIGHT
}

interface UseResizableTextareaIProps {
  enabled: boolean
  textareaRef: RefObject<HTMLTextAreaElement>
}

/**
 * Drag-to-resize for the multiline text input (ENG-35893). Height stays `null`
 * until the operator actually drags, so an untouched input keeps whatever
 * sizing it had — including auto-resize, which the caller turns off once a
 * manual height exists.
 */
export const useResizableTextarea = ({
  enabled,
  textareaRef,
}: UseResizableTextareaIProps) => {
  const [height, setHeight] = useState<number | null>(null)
  const [isResizing, setIsResizing] = useState(false)
  const dragStartRef = useRef<{
    y: number
    height: number
    pointerId: number | null
  } | null>(null)

  const resizeBy = useCallback(
    (delta: number) => {
      const textarea = textareaRef.current
      if (!textarea) return

      setHeight(Math.max(getMinHeight(textarea), textarea.offsetHeight + delta))
    },
    [textareaRef]
  )

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const textarea = textareaRef.current
      // jsdom has no PointerEvent, so `button` can be absent under test —
      // nullish-coalesce to the primary button rather than bailing out.
      if (!enabled || !textarea || (event.button ?? 0) !== 0) return

      dragStartRef.current = {
        y: event.clientY,
        height: textarea.offsetHeight,
        // Pins the gesture to the pointer that started it, so a second finger
        // can neither jump the height nor end the drag. Absent in jsdom.
        pointerId: event.pointerId ?? null,
      }
      setIsResizing(true)
      // The textarea must not take focus from the pointer landing on the
      // handle, and text selection during the drag reads as a broken gesture.
      event.preventDefault()
    },
    [enabled, textareaRef]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!enabled) return
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      event.preventDefault()
      resizeBy(event.key === 'ArrowDown' ? KEYBOARD_STEP : -KEYBOARD_STEP)
    },
    [enabled, resizeBy]
  )

  useEffect(() => {
    if (!isResizing) return

    // Disabling the input unmounts the handle, so no pointerup can reach it.
    // End the drag here instead of leaving the window listeners armed.
    if (!enabled) {
      dragStartRef.current = null
      setIsResizing(false)
      return
    }

    const isDragPointer = (event: globalThis.PointerEvent) =>
      (event.pointerId ?? null) === dragStartRef.current?.pointerId

    const onPointerMove = (event: globalThis.PointerEvent) => {
      const textarea = textareaRef.current
      const dragStart = dragStartRef.current
      if (!textarea || !dragStart || !isDragPointer(event)) return

      const next = dragStart.height + (event.clientY - dragStart.y)
      setHeight(Math.max(getMinHeight(textarea), next))
    }

    const onPointerUp = (event: globalThis.PointerEvent) => {
      if (!isDragPointer(event)) return

      dragStartRef.current = null
      setIsResizing(false)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [enabled, isResizing, textareaRef])

  return {
    height,
    isResizing,
    handleProps: {
      onKeyDown: handleKeyDown,
      onPointerDown: handlePointerDown,
    },
  }
}
